/**
 * The Dashboard as head office's command centre (Gary, 15 Oct 2026): Summary, Leads
 * (WhatsApp Live), Service Logs and Certificates as tabs, so leads can be watched and
 * service logs and certificates approved and sent from one place.
 *
 * Super admins only while Gary tests it; everyone else gets today's Dashboard unchanged.
 * Each tab reuses the module's own screen (WhatsApp Live, the Service Logs board, the
 * certification board), so every action is the module's own. Plan: docs/TODO.md
 * "COMMAND CENTRE DASHBOARD AND WHATSAPP SPLIT".
 */

import { useEffect, useState } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import { useToast } from '../../../components/Toast';
import Dashboard from './Dashboard';
import ServiceLogsPage from './ServiceLogsPage';
import Certification from './Certification';
import LiveDesk, { needsAttention } from './whatsapp/LiveDesk';
import { onBoard, useLiveChats } from './whatsapp/liveData';

type Tab = 'summary' | 'leads' | 'service_logs' | 'certificates';

const TABS: { id: Tab; label: string }[] = [
  { id: 'summary', label: 'Summary' },
  { id: 'leads', label: 'Leads' },
  { id: 'service_logs', label: 'Service Logs' },
  { id: 'certificates', label: 'Certificates' },
];

const TAB_KEY = 'dashboard-tab';

function readTab(): Tab {
  try {
    const saved = localStorage.getItem(TAB_KEY);
    return TABS.some((t) => t.id === saved) ? (saved as Tab) : 'summary';
  } catch {
    return 'summary';
  }
}

export default function CommandCentre() {
  const { isSuperAdmin } = useAuth();
  return isSuperAdmin ? <HeadOfficeDashboard /> : <Dashboard />;
}

function HeadOfficeDashboard() {
  const { showToast } = useToast();
  const [tab, setTab] = useState<Tab>(readTab);
  const { chats, loading, error, reload } = useLiveChats();

  useEffect(() => { if (error) showToast(`Chats did not load: ${error}`, 'error'); }, [error, showToast]);

  function choose(t: Tab) {
    setTab(t);
    try { localStorage.setItem(TAB_KEY, t); } catch { /* private window: not remembered */ }
  }

  // Both counts on the Leads tab: need attention now, and everything in play (as on WhatsApp Live).
  const attention = needsAttention(chats);
  const inPlay = chats.filter(onBoard).length;

  return (
    <div className={tab === 'leads' ? 'wa-page wa-page--fill' : undefined}>
      <div className="page-tabs">
        {TABS.map((t) => (
          <button key={t.id} type="button" className={`page-tab ${tab === t.id ? 'active' : ''}`} onClick={() => choose(t.id)}>
            {t.label}
            {t.id === 'leads' && (
              <>
                {attention > 0 && <span className="tab-badge" title="Need attention now: unassigned, overdue or escalated">{attention}</span>}
                <span className="tab-badge secondary" title="In play">{inPlay}</span>
              </>
            )}
          </button>
        ))}
      </div>

      {tab === 'summary' && (
        <div className="dashboard-home">
          <div className="dashboard-section">
            <div className="dashboard-section-header">
              <h3 className="dashboard-section-title">
                <span className="dashboard-section-icon">💬</span>
                WhatsApp leads
              </h3>
              <button type="button" className="btn btn-secondary btn-sm" onClick={() => choose('leads')}>Open Leads</button>
            </div>
            <div className="dashboard-section-body">
              <p className="dashboard-empty-message">
                {loading
                  ? 'Loading...'
                  : attention > 0
                    ? `${attention} chat${attention === 1 ? '' : 's'} need attention now (unassigned, overdue or escalated). ${inPlay} in play.`
                    : `All clear. ${inPlay} in play.`}
              </p>
            </div>
          </div>
          <Dashboard />
        </div>
      )}
      {tab === 'leads' && <LiveDesk chats={chats} loading={loading} reload={reload} />}
      {tab === 'service_logs' && <ServiceLogsPage boardOnly />}
      {tab === 'certificates' && <Certification boardOnly />}
    </div>
  );
}
